// client/src/modules/forms/routes.tsx
import React from 'react';
import { Navigate, type RouteObject } from 'react-router-dom';
import { ProtectedRoute } from '../../components/ProtectedRoute';
import { FormConsumerProvider } from '../../contexts/FormConsumerContext';
import { lazyWithRetry } from '../../lib/lazyWithRetry';
import EmbedNotFound from '../../pages/Forms/EmbedNotFound';

// Lazy load all Forms pages for code splitting
const FormsDashboard = lazyWithRetry(() => import('../../pages/Forms/FormsDashboard'));
const FormSelector = lazyWithRetry(() => import('../../pages/Forms/FormSelector'));
const FormFiller = lazyWithRetry(() => import('../../pages/Forms/FormFiller'));
const FormTemplateList = lazyWithRetry(() => import('../../pages/Forms/FormTemplateList'));
const FormBuilder = lazyWithRetry(() => import('../../pages/Forms/FormBuilder'));
const FormSubmissionList = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionList'));
const FormSubmissionDetail = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionDetail'));
const FormSubmissionEdit = lazyWithRetry(() => import('../../pages/Forms/FormSubmissionEdit'));
const DocumentsPage = lazyWithRetry(() => import('../../pages/Forms/DocumentsPage'));
const DocumentRequestReview = lazyWithRetry(() => import('../../pages/Forms/DocumentRequestReview'));
const PublicFormPage = lazyWithRetry(() => import('../../pages/Forms/PublicFormPage'));
const EmbedFormPage = lazyWithRetry(() => import('../../pages/Forms/EmbedFormPage'));
const ServiceReportLauncher = lazyWithRetry(() => import('../../pages/Forms/ServiceReportLauncher'));
const TicketFormAssignment = lazyWithRetry(() => import('../../pages/Forms/TicketFormAssignment'));
const FormReportPage = lazyWithRetry(() => import('../../pages/Forms/FormReportPage'));
const OrgProfilePage = lazyWithRetry(() => import('../../pages/Forms/OrgProfilePage'));
const MasterDataReview = lazyWithRetry(() => import('../../pages/Forms/MasterDataReview'));

/**
 * Wraps consumer pages with FormConsumerProvider so navigation paths
 * resolve to /forms/* (the P8 module's own routes).
 */
function FormsConsumer({ children }: { children: React.ReactNode }) {
  return (
    <FormConsumerProvider basePath="/forms" moduleLabel="Forms" moduleId="P8">
      {children}
    </FormConsumerProvider>
  );
}

/**
 * Forms module routes - relative to /forms prefix
 */
export const routes: RouteObject[] = [
  {
    index: true,
    element: <ProtectedRoute><FormsDashboard /></ProtectedRoute>,
  },
  // Initiative #2: review nameplate values derived from past service reports
  {
    path: 'master-data-review',
    element: <ProtectedRoute><MasterDataReview /></ProtectedRoute>,
  },
  // Public form route - no authentication required
  {
    path: 'public/:slug',
    element: <PublicFormPage />,
  },
  // Embed form route - minimal chrome for iframe embedding
  {
    path: 'embed/:slug',
    element: <EmbedFormPage />,
  },
  // Anything else under /forms/embed/ is served with frameable embed headers, so it
  // must NEVER fall through to the '*' <Navigate> below (that renders the logged-in
  // app inside the website's frame). Static, eager, never navigates.
  {
    path: 'embed/*',
    element: <EmbedNotFound />,
  },
  // Deep link: /forms/start-report?serviceId=S250422-T3A
  // Portable URL for AppSheet, email, and SMS links
  {
    path: 'start-report',
    element: <ProtectedRoute><ServiceReportLauncher /></ProtectedRoute>,
  },
  {
    path: 'fill',
    element: <ProtectedRoute><FormsConsumer><FormSelector /></FormsConsumer></ProtectedRoute>,
  },
  {
    path: 'fill/:templateId',
    element: <ProtectedRoute><FormsConsumer><FormFiller /></FormsConsumer></ProtectedRoute>,
  },
  // Documents — Smart Doc Fill + Company Info (P8 admin only)
  {
    path: 'documents',
    element: <ProtectedRoute><DocumentsPage /></ProtectedRoute>,
  },
  {
    path: 'documents/review/:requestId',
    element: <ProtectedRoute><DocumentRequestReview /></ProtectedRoute>,
  },
  // Manual ticket->form assignment (Forms Platform Phase 1c, WIP)
  {
    path: 'ticket-assignments/:serviceTicketId',
    element: <ProtectedRoute><TicketFormAssignment /></ProtectedRoute>,
  },
  // Template management routes (P8 admin only)
  {
    path: 'templates',
    element: <ProtectedRoute><FormTemplateList /></ProtectedRoute>,
  },
  {
    path: 'templates/new',
    element: <ProtectedRoute><FormBuilder /></ProtectedRoute>,
  },
  {
    path: 'templates/:templateId/edit',
    element: <ProtectedRoute><FormBuilder /></ProtectedRoute>,
  },
  // Consumer routes (also available in host modules)
  {
    path: 'submissions',
    element: <ProtectedRoute><FormsConsumer><FormSubmissionList /></FormsConsumer></ProtectedRoute>,
  },
  {
    path: 'submissions/:submissionId',
    element: <ProtectedRoute><FormsConsumer><FormSubmissionDetail /></FormsConsumer></ProtectedRoute>,
  },
  {
    path: 'submissions/:submissionId/edit',
    element: <ProtectedRoute><FormsConsumer><FormSubmissionEdit /></FormsConsumer></ProtectedRoute>,
  },
  {
    path: 'submissions/:submissionId/report',
    element: <ProtectedRoute><FormsConsumer><FormReportPage /></FormsConsumer></ProtectedRoute>,
  },
  {
    path: 'org-profile',
    element: <ProtectedRoute><OrgProfilePage /></ProtectedRoute>,
  },
  {
    /**
     * Unknown Forms path -> the Forms dashboard (ISS-469).
     *
     * This slot used to render the About page, which is why /forms/about and
     * /forms/anything-else were the same screen. With About gone the catch-all
     * needs a destination, and a redirect to the module home is what most
     * modules here already do (crm, support, billing, marketing, recruiting,
     * huddle, ai-systems, explorer). It also preserves the one property the old
     * catch-all had: a stale Forms bookmark lands on a working page rather than
     * a 404 or a blank screen.
     */
    path: '*',
    element: <Navigate to="/forms" replace />,
  },
];
